/** Staff: firm billing, the Tax sections (organizers, extensions, refund status), and data import/export settings. */
import { formatCents, latestOpenYear } from "@clientflow/shared";
import { ask } from "@/components/dialogs";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { InvoiceList, type Aging, type InvoicePage } from "@/components/billing";
import { Empty, ListSearch, Loading, matches, PageHeader, Pager, Section, Segmented, StatRow, usePaged, useServerPaged } from "@/components/page";
import { FormLibrary } from "@/components/organizer-builder";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, ApiError, errorMessage } from "@/lib/api";
import { defaultTaxYear, formatDate, localToday, plural } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import type { Client } from "@/lib/types";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

const select = "h-9 rounded-md border border-input bg-background px-2 text-sm";
const TAX = { label: "Tax", href: "/tax" };

// --- Billing ------------------------------------------------------------------------------

/** Picks the client, then opens that client's Billing tab with a new invoice started. */
function NewInvoiceButton() {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { data } = useQuery<{ items: Client[] }>({ queryKey: [`/api/clients?status=active&limit=20${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ""}`], enabled: open });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" /> New invoice
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New invoice</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="inv-client">Client</Label>
          <Input id="inv-client" autoFocus placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} />
          <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border">
            {(data?.items ?? []).map((c) => (
              <li key={c.id}>
                <button className="w-full px-3 py-2 text-left text-sm hover:bg-muted/60" onClick={() => navigate(`/clients/${c.id}?tab=billing&new=1`)}>
                  {c.displayName}
                </button>
              </li>
            ))}
            {data && !data.items.length && <li className="px-3 py-2 text-sm text-muted-foreground">No active clients match.</li>}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BillingPage({ me }: { me: Me }) {
  const [status, setStatus] = useState("open");
  const [q, setQ] = useState("");
  const { offset, size, paged } = useServerPaged(`${status}|${q.trim()}`);
  const params = new URLSearchParams({ offset: String(offset), limit: String(size) });
  if (status) params.set("status", status);
  if (q.trim()) params.set("q", q.trim());
  const { data, isLoading } = useQuery<InvoicePage>({ queryKey: [`/api/invoices?${params}`], placeholderData: (prev) => prev });
  // Receivables aging over every open invoice: balance due by days past the due date.
  const { data: aging } = useQuery<Aging>({ queryKey: ["/api/invoices/aging"] });
  const buckets = [aging?.currentCents ?? 0, aging?.days1to30Cents ?? 0, aging?.days31to60Cents ?? 0, aging?.over60Cents ?? 0];
  return (
    <>
      <PageHeader
        title="Billing"
        description="Invoices, payments and accounts receivable."
        actions={
          <>
            <select aria-label="Show invoices" className={select} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="open">Open</option>
              <option value="draft">Drafts</option>
              <option value="paid">Paid</option>
              <option value="void">Void</option>
              <option value="">All invoices</option>
            </select>
            {hasPermission(me, "billing.write") && <NewInvoiceButton />}
          </>
        }
      />
      <StatRow
        stats={[
          { label: "Current", value: formatCents(buckets[0]!) },
          { label: "1–30 days past due", value: formatCents(buckets[1]!), tone: buckets[1] ? "warning" : "default" },
          { label: "31–60 days past due", value: formatCents(buckets[2]!), tone: buckets[2] ? "danger" : "default" },
          { label: "Over 60 days past due", value: formatCents(buckets[3]!), tone: buckets[3] ? "danger" : "default" },
        ]}
      />
      <ListSearch value={q} onChange={setQ} placeholder="Search by client or invoice number" className="mb-4" />
      {isLoading ? <Loading /> : <InvoiceList invoices={data?.items ?? []} me={me} paged={paged(data?.total ?? 0)} empty={q.trim() ? "No invoices match your search." : "No invoices match this filter."} />}
    </>
  );
}

// --- Tax ----------------------------------------------------------------------------------

type FilingRow = { id: string; clientId: string; clientName: string; taxYear: number; jurisdiction: string; form: string; extensionFiledOn: string | null; refundCents: number | null; acceptedOn: string | null; clientReportedMissingAt: string | null };

function FilingTable({ rows, columns, empty, label }: { label: string; rows: FilingRow[]; columns: Array<{ label: string; value: (r: FilingRow) => string; align?: "right" }>; empty: string }) {
  const [search, setSearch] = useState("");
  const shown = rows.filter((r) => matches(search, r.clientName, r.form, r.jurisdiction));
  const paged = usePaged(shown, { resetKey: search });
  if (!rows.length) return <Empty>{empty}</Empty>;
  return (
    <div className="space-y-3">
      <ListSearch value={search} onChange={setSearch} placeholder="Search by client or form" />
      {!shown.length ? (
        <Empty>No returns match the search.</Empty>
      ) : (
        <TableScroll label={label} className="rounded-lg border border-card-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 font-medium">Client</th>
                <th className="px-4 py-2.5 font-medium">Return</th>
                {columns.map((c) => (
                  <th key={c.label} className={`px-4 py-2.5 font-medium ${c.align === "right" ? "text-right" : ""}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paged.items.map((r) => (
                <tr key={r.id} className="hover:bg-muted/40">
                  <td className="px-4 py-2.5">
                    <Link href={`/clients/${r.clientId}?tab=tax`} className="font-medium hover:underline">
                      {r.clientName}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {r.taxYear} {r.jurisdiction === "federal" ? "Federal" : r.jurisdiction} {r.form}
                  </td>
                  {columns.map((c) => (
                    <td key={c.label} className={`px-4 py-2.5 tabular ${c.align === "right" ? "text-right" : ""}`}>
                      {c.value(r)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      <Pager paged={paged} noun="returns" />
    </div>
  );
}

/** The value a few moments after it last changed, so a search box doesn't query on every keystroke. */
function useSettled<T>(value: T, ms = 300): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

type Recipient = { id: string; displayName: string; clientCode: string | null };

/**
 * Sends an organizer to chosen tax clients, or to every active tax client matching the search.
 * The list comes from the server a page at a time, so it works the same with 50 clients or 5,000.
 */
function BulkOrganizers() {
  const { data: templates } = useQuery<Array<{ id: string; name: string; isActive: boolean; kind?: string }>>({ queryKey: ["/api/organizer-templates"] });
  const [templateId, setTemplateId] = useState("");
  const [taxYear, setTaxYear] = useState(String(defaultTaxYear()));
  const [search, setSearch] = useState("");
  const [notSent, setNotSent] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  // Every client matching the current search and filter, not just the ones ticked.
  const [allMatching, setAllMatching] = useState(false);
  const year = Number(taxYear) >= 2000 && Number(taxYear) <= 2100 ? Number(taxYear) : defaultTaxYear();
  const q = useSettled(search.trim());
  const filterKey = `${q}|${notSent}|${year}`;
  const pages = useServerPaged(filterKey);
  useEffect(() => setAllMatching(false), [filterKey]);
  const { data, isLoading, error, isPlaceholderData } = useQuery<{ items: Recipient[]; total: number }>({
    queryKey: [`/api/tax/organizer-recipients?taxYear=${year}${q ? `&q=${encodeURIComponent(q)}` : ""}${notSent ? "&notSent=true" : ""}&offset=${pages.offset}&limit=${pages.size}`],
    placeholderData: (prev) => prev,
  });
  const send = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send<{ created: number; skipped: Array<{ clientId: string; reason: string }> }>("POST", "/api/organizers/bulk", {
        ...(allMatching ? { allMatching: { ...(q ? { q } : {}), ...(notSent ? { notSent: true } : {}) } } : { clientIds: [...picked] }),
        templateId,
        taxYear: year,
      }),
    onSuccess: () => {
      setPicked(new Set());
      setAllMatching(false);
    },
  });
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const count = allMatching ? total : picked.size;
  const pageAllPicked = rows.length > 0 && rows.every((c) => picked.has(c.id));
  const template = templates?.find((t) => t.id === templateId);
  const toggle = (id: string, on: boolean) => {
    const next = new Set(picked);
    if (on) next.add(id);
    else next.delete(id);
    setPicked(next);
  };
  const confirmSend = async () => {
    if (
      await ask({
        title: `Send the organizer to ${plural(count, "client")}?`,
        description: `“${template?.name ?? "The organizer"}” for ${year} goes to ${allMatching ? `every active tax client${q ? ` matching “${q}”` : ""}${notSent ? " with no organizer for the year yet" : ""}` : "the selected clients"}, and each receives an email. Clients who already have this organizer for ${year} are skipped.`,
        confirmLabel: "Send organizers",
      })
    )
      send.mutate();
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor="org-template">Organizer</Label>
          <select id="org-template" className={cn(select, "w-full sm:w-72")} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">Select an organizer</option>
            {templates
              ?.filter((t) => t.isActive && (t.kind ?? "organizer") === "organizer")
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="org-year">Tax year</Label>
          <Input id="org-year" className="w-full sm:w-24" inputMode="numeric" value={taxYear} onChange={(e) => setTaxYear(e.target.value.replace(/\D/g, "").slice(0, 4))} />
        </div>
        <Button disabled={!templateId || !count || send.isPending || (allMatching && isPlaceholderData)} onClick={confirmSend}>
          {count ? `Send to ${plural(count, "client")}` : "Send organizers"}
        </Button>
      </div>
      {send.data && (
        <p className="rounded-md bg-muted px-3 py-2 text-sm">
          Organizers sent: {send.data.created}. {send.data.skipped.length > 0 && `Not sent: ${send.data.skipped.length} (${[...new Set(send.data.skipped.map((s) => s.reason))].join(", ")}).`}
        </p>
      )}
      {send.error && <p className="text-sm text-destructive">{errorMessage(send.error)}</p>}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <ListSearch value={search} onChange={setSearch} placeholder="Search tax clients" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={notSent} onChange={(e) => setNotSent(e.target.checked)} />
          Only clients with no organizer for {year} yet
        </label>
      </div>
      {isLoading ? (
        <Loading />
      ) : error ? (
        <Empty>{errorMessage(error)}</Empty>
      ) : !total ? (
        <Empty>{q || notSent ? "No active tax clients match." : "No active clients are engaged for tax services."}</Empty>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {allMatching ? (
              <span className="font-medium">All {plural(total, "matching client")} are selected.</span>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={() => setPicked((prev) => new Set([...prev, ...rows.map((c) => c.id)]))} disabled={pageAllPicked}>
                  Select all on this page
                </Button>
                <Button size="sm" variant="outline" disabled={isPlaceholderData || search.trim() !== q} onClick={() => setAllMatching(true)}>
                  Select all {total.toLocaleString("en-US")} matching
                </Button>
                {picked.size > 0 && <span className="text-muted-foreground">{picked.size} selected</span>}
              </>
            )}
            {(allMatching || picked.size > 0) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setPicked(new Set());
                  setAllMatching(false);
                }}
              >
                Clear selection
              </Button>
            )}
          </div>
          <ul className="grid gap-1 rounded-lg border border-border p-2 sm:grid-cols-2">
            {rows.map((c) => (
              <li key={c.id}>
                <label className={cn("flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/50", allMatching && "text-muted-foreground")}>
                  <input type="checkbox" disabled={allMatching} checked={allMatching || picked.has(c.id)} onChange={(e) => toggle(c.id, e.target.checked)} />
                  <span className="min-w-0 truncate">{c.displayName}</span>
                  {c.clientCode && <span className="text-xs text-muted-foreground">{c.clientCode}</span>}
                </label>
              </li>
            ))}
          </ul>
          <Pager paged={pages.paged(total)} noun="clients" />
        </div>
      )}
    </div>
  );
}

type Stage = "not_sent" | "sent" | "in_progress" | "documents" | "ready";
type TrackerRow = {
  client: { id: string; displayName: string; kind: string; assignedUserId: string | null };
  organizer: { id: string; title: string; status: string; answered: number; total: number; sentAt: string; submittedAt: string | null } | null;
  documents: { requested: number; received: number };
  stage: Stage;
};
const STAGES: Array<[Stage, string]> = [
  ["not_sent", "Not sent"],
  ["sent", "Sent"],
  ["in_progress", "In progress"],
  ["documents", "Submitted, documents due"],
  ["ready", "Ready to prepare"],
];
const STEP_LABELS = ["Sent", "Answering", "Submitted", "Documents", "Ready"];
// The step each stage is on: earlier steps are done (ready = all five done).
const stepIndex: Record<Stage, number> = { not_sent: -1, sent: 1, in_progress: 1, documents: 3, ready: 5 };

/** Where every tax client's organizer stands for a tax year, with reminders for the unfinished. */
function OrganizerTracker({ me }: { me: Me }) {
  const [year, setYear] = useState(String(defaultTaxYear()));
  const [stage, setStage] = useState<Stage | "">("");
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const taxYear = Number(year) || defaultTaxYear();
  const { data, isLoading, error } = useQuery<TrackerRow[]>({ queryKey: [`/api/tax/organizer-tracker?taxYear=${taxYear}`] });
  const remind = useMutation({
    meta: { inlineError: true },
    mutationFn: (ids?: string[]) => api.send<{ reminded: number }>("POST", "/api/tax/organizer-tracker/remind", { taxYear, ...(ids ? { organizerIds: ids } : {}) }),
    onSuccess: () => setPicked(new Set()),
  });
  const rows = (data ?? []).filter((r) => (!stage || r.stage === stage) && matches(search, r.client.displayName, r.organizer?.title));
  const paged = usePaged(rows, { resetKey: `${stage}|${search}|${taxYear}` });
  const unfinished = (data ?? []).filter((r) => r.stage === "sent" || r.stage === "in_progress");
  const canRemind = hasPermission(me, "requests.write");
  const remindable = (r: TrackerRow) => r.organizer?.status === "open";
  const pageIds = paged.items.filter(remindable).map((r) => r.organizer!.id);
  const matchingIds = rows.filter(remindable).map((r) => r.organizer!.id);
  const selectIds = (ids: string[]) => setPicked((prev) => new Set([...prev, ...ids]));
  return (
    <Section
      title="Organizer tracker"
      description="Every tax client's organizer for the year: sent, being answered, submitted, documents in, ready to prepare."
      action={
        <label className="flex items-center gap-2 whitespace-nowrap text-sm text-muted-foreground">
          Tax year
          <Input
            aria-label="Tax year"
            className="h-9 w-24"
            inputMode="numeric"
            value={year}
            onChange={(e) => {
              setYear(e.target.value.replace(/\D/g, "").slice(0, 4));
              setPicked(new Set());
            }}
          />
        </label>
      }
    >
      {isLoading ? (
        <Loading />
      ) : error ? (
        <Empty>{errorMessage(error)}</Empty>
      ) : !data?.length ? (
        <Empty>No clients are engaged for Tax.</Empty>
      ) : (
        <div className="space-y-4">
          <Segmented
            label="Filter by stage"
            value={stage}
            onChange={setStage}
            options={[["", "All"] as [Stage | "", string], ...STAGES].map(([k, l]) => ({ value: k, label: l, count: k ? data.filter((r) => r.stage === k).length : data.length }))}
          />
          <ListSearch value={search} onChange={setSearch} placeholder="Search by client" />
          {canRemind && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Button size="sm" variant="outline" disabled={!picked.size || remind.isPending} onClick={() => remind.mutate([...picked])}>
                Remind selected ({picked.size})
              </Button>
              <Button size="sm" variant="ghost" disabled={!pageIds.length || pageIds.every((id) => picked.has(id))} onClick={() => selectIds(pageIds)}>
                Select all on this page
              </Button>
              {matchingIds.length > pageIds.length && (
                <Button size="sm" variant="ghost" disabled={matchingIds.every((id) => picked.has(id))} onClick={() => selectIds(matchingIds)}>
                  Select all {matchingIds.length} matching
                </Button>
              )}
              {picked.size > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())}>
                  Clear selection
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={!unfinished.length || remind.isPending}
                onClick={async () => {
                  if (await ask({ title: `Remind ${unfinished.length} client${unfinished.length > 1 ? "s" : ""}?`, description: `Everyone with an unfinished ${taxYear} organizer gets an email reminder. A client is reminded at most once a day.`, confirmLabel: "Send reminders" })) remind.mutate(undefined);
                }}
              >
                Remind everyone unfinished ({unfinished.length})
              </Button>
              {remind.data && <span className="text-emerald-700 dark:text-emerald-400">{remind.data.reminded} reminded.</span>}
              {remind.error && <span className="text-destructive">{errorMessage(remind.error)}</span>}
            </div>
          )}
          {!rows.length ? (
            <Empty>No clients match.</Empty>
          ) : (
          <TableScroll label="Organizer tracker table">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr className="border-b border-border">
                  {canRemind && <th className="w-8 py-2">
                      <span className="sr-only">Select</span>
                    </th>}
                  <th className="py-2 pr-4 font-medium">Client</th>
                  <th className="py-2 pr-4 font-medium">Steps</th>
                  <th className="py-2 pr-4 text-right font-medium">Answered</th>
                  <th className="py-2 pr-4 text-right font-medium">Documents</th>
                  <th className="py-2 font-medium">Sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {paged.items.map((r) => {
                  const at = stepIndex[r.stage];
                  return (
                    <tr key={r.organizer?.id ?? r.client.id}>
                      {canRemind && (
                        <td className="py-2.5">
                          {remindable(r) && (
                            <input
                              type="checkbox"
                              aria-label={`Select ${r.client.displayName}`}
                              checked={picked.has(r.organizer!.id)}
                              onChange={(e) => {
                                const next = new Set(picked);
                                if (e.target.checked) next.add(r.organizer!.id);
                                else next.delete(r.organizer!.id);
                                setPicked(next);
                              }}
                            />
                          )}
                        </td>
                      )}
                      <td className="py-2.5 pr-4">
                        <Link href={`/clients/${r.client.id}?tab=tax`} className="font-medium hover:underline">
                          {r.client.displayName}
                        </Link>
                        <p className="text-xs text-muted-foreground">{r.organizer?.title ?? "No organizer sent"}</p>
                      </td>
                      <td className="py-2.5 pr-4">
                        {at < 0 ? (
                          <span className="text-xs text-muted-foreground">Not sent</span>
                        ) : (
                          <ol className="flex items-center gap-1" aria-label={`Stage: ${STAGES.find(([k]) => k === r.stage)![1]}`}>
                            {STEP_LABELS.map((l, i) => (
                              <li key={l} className="flex items-center gap-1">
                                <span
                                  title={l}
                                  className={cn(
                                    "flex h-5 items-center rounded-full px-2 text-[11px] font-medium",
                                    i < at ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200" : i === at ? "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200" : "bg-secondary text-muted-foreground",
                                  )}
                                >
                                  {l}
                                </span>
                                {i < STEP_LABELS.length - 1 && <span className="h-px w-2 bg-border" aria-hidden />}
                              </li>
                            ))}
                          </ol>
                        )}
                      </td>
                      <td className="tabular py-2.5 pr-4 text-right">{r.organizer ? `${r.organizer.answered} of ${r.organizer.total}` : "—"}</td>
                      <td className="tabular py-2.5 pr-4 text-right">{r.organizer?.status === "submitted" ? (r.documents.requested ? `${r.documents.received} of ${r.documents.requested}` : "None needed") : "—"}</td>
                      <td className="py-2.5 text-muted-foreground">{r.organizer ? formatDate(r.organizer.sentAt) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
          )}
          <Pager paged={paged} noun="clients" />
        </div>
      )}
    </Section>
  );
}

const ORGANIZER_VIEWS = [
  { value: "tracker", label: "Tracker" },
  { value: "send", label: "Send organizers" },
  { value: "templates", label: "Templates" },
] as const;
type OrganizerView = (typeof ORGANIZER_VIEWS)[number]["value"];

export function TaxOrganizersPage({ me }: { me: Me }) {
  const [, navigate] = useLocation();
  const asked = new URLSearchParams(useSearch()).get("view");
  const view: OrganizerView = ORGANIZER_VIEWS.some((v) => v.value === asked) ? (asked as OrganizerView) : "tracker";
  return (
    <>
      <PageHeader crumbs={[TAX]} title="Organizers" description="Track every client's organizer, send organizers in bulk, and manage the organizer templates." />
      <div className="space-y-6">
        <Segmented label="Organizer views" value={view} onChange={(v) => navigate(v === "tracker" ? "/tax/organizers" : `/tax/organizers?view=${v}`, { replace: true })} options={ORGANIZER_VIEWS} />
        {view === "tracker" && <OrganizerTracker me={me} />}
        {view === "send" &&
          (hasPermission(me, "requests.write") ? (
            <Section title="Send organizers" description="Choose clients, or select everyone matching the search. Clients who already have the organizer for the year are skipped.">
              <BulkOrganizers />
            </Section>
          ) : (
            <Empty>You do not have permission to send organizers.</Empty>
          ))}
        {view === "templates" && <FormLibrary kind="organizer" me={me} />}
      </div>
    </>
  );
}

export function TaxExtensionsPage() {
  // Extensions filed this year are for last year's returns.
  const [year, setYear] = useState(String(new Date().getFullYear() - 1));
  const { data: ext, isLoading } = useQuery<FilingRow[]>({ queryKey: [`/api/tax/extensions?taxYear=${Number(year) || 2000}`] });
  return (
    <>
      <PageHeader
        crumbs={[TAX]}
        title="Extensions"
        description="Returns on extension (Form 4868 / 7004) that have not yet been filed."
        actions={
          <label className="flex items-center gap-2 whitespace-nowrap text-sm text-muted-foreground">
            Tax year
            <Input aria-label="Tax year" className="h-9 w-24" inputMode="numeric" value={year} onChange={(e) => setYear(e.target.value)} />
          </label>
        }
      />
      {isLoading ? (
        <Loading />
      ) : (
        <FilingTable label="Extended returns table" rows={ext ?? []} empty={`No extended ${year} returns are awaiting filing.`} columns={[{ label: "Extension filed", value: (r) => formatDate(r.extensionFiledOn) }]} />
      )}
    </>
  );
}

export function TaxRefundsPage() {
  const { data, isLoading } = useQuery<FilingRow[]>({ queryKey: ["/api/tax/refund-followups"] });
  return (
    <>
      <PageHeader crumbs={[TAX]} title="Refund Status" description="Clients who reported a refund as not received. Open the client's Tax tab to record the follow-up." />
      {isLoading ? (
        <Loading />
      ) : (
        <FilingTable
          label="Refund inquiries table"
          rows={data ?? []}
          empty="No refund inquiries are open."
          columns={[
            { label: "Refund amount", value: (r) => (r.refundCents ? formatCents(r.refundCents) : "—"), align: "right" },
            { label: "Reported not received", value: (r) => formatDate(r.clientReportedMissingAt) },
          ]}
        />
      )}
    </>
  );
}

// --- Import, export and firm settings (Settings tabs) ------------------------------------------

type Preview = {
  headers: string[];
  mapping: Record<string, string | null>;
  ignoredSensitiveColumns: string[];
  totals: { rows: number; ready: number; duplicates: number; errors: number };
  sample: Array<{ row: number; client: { displayName: string; kind: string; email?: string; clientCode?: string; modules: string[] } | null; error: string | null; duplicateOf: string | null }>;
  errors: Array<{ row: number; error: string }>;
};

const FIELD_LABELS: Record<string, string> = {
  displayName: "Name",
  firstName: "First name",
  lastName: "Last name",
  legalName: "Legal / business name",
  kind: "Individual or business",
  email: "Email",
  phone: "Phone",
  clientCode: "Client code",
  notes: "Notes",
  services: "Services / tags",
};

async function postFile<T>(url: string, file: File | File[], fields: Record<string, string> = {}, field = "file"): Promise<T> {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) form.append(k, v);
  for (const f of Array.isArray(file) ? file : [file]) form.append(field, f);
  const res = await fetch(url, { method: "POST", credentials: "same-origin", headers: { "x-clientflow-csrf": "1" }, body: form });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.error?.code ?? "error", body.error?.message ?? "The request could not be completed. Please try again.", body.error?.details);
  return body as T;
}

export function ImportClients() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [mapping, setMapping] = useState<Record<string, string | null>>({});
  const [onDuplicate, setOnDuplicate] = useState("skip");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ created: number; updated: number; skipped: number; errors: Array<{ row: number; error: string }> } | null>(null);
  const run = async (f: File, m?: Record<string, string | null>) => {
    setError(null);
    setResult(null);
    try {
      const p = await postFile<Preview>("/api/clients/import/preview", f, m ? { mapping: JSON.stringify(m) } : {});
      setPreview(p);
      setMapping(p.mapping);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const commit = useMutation({
    meta: { inlineError: true },
    mutationFn: () => postFile<NonNullable<typeof result>>("/api/clients/import/commit", file!, { mapping: JSON.stringify(mapping), onDuplicate }),
    onSuccess: (r) => {
      setResult(r);
      setPreview(null);
    },
  });
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Upload a client list exported from TaxDome, OLT Pro or a spreadsheet (.csv). A preview is shown before anything is saved. Columns containing SSNs, EINs or bank account numbers are always ignored.
      </p>
      <input
        type="file"
        accept=".csv"
        aria-label="Client CSV"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          setFile(f);
          if (f) void run(f);
        }}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
      {preview && file && (
        <div className="space-y-4">
          {preview.ignoredSensitiveColumns.length > 0 && (
            <p className="rounded-md border border-accent/60 bg-accent/10 px-3 py-2 text-sm">Ignored for safety: {preview.ignoredSensitiveColumns.join(", ")}</p>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            {preview.headers
              .filter((h) => !preview.ignoredSensitiveColumns.includes(h))
              .map((h) => (
                <label key={h} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-1.5 text-sm">
                  <span className="truncate">{h}</span>
                  <select className={select} value={mapping[h] ?? ""} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value || null })}>
                    <option value="">Ignore column</option>
                    {Object.entries(FIELD_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => run(file, mapping)}>
            Refresh preview
          </Button>
          <p className="text-sm">
            {plural(preview.totals.rows, "row")}: <span className="font-medium">{preview.totals.ready} new</span>, {preview.totals.duplicates} already in ClientFlow, {preview.totals.errors} with errors.
          </p>
          <TableScroll label="Client import preview table" className="rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Row</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Code</th>
                  <th className="px-3 py-2">Result</th>
                </tr>
              </thead>
              <tbody>
                {preview.sample.map((r) => (
                  <tr key={r.row} className="border-t border-border">
                    <td className="px-3 py-1.5">{r.row}</td>
                    <td className="px-3 py-1.5">{r.client?.displayName ?? "—"}</td>
                    <td className="px-3 py-1.5 capitalize">{r.client?.kind ?? ""}</td>
                    <td className="px-3 py-1.5">{r.client?.email ?? ""}</td>
                    <td className="px-3 py-1.5">{r.client?.clientCode ?? ""}</td>
                    <td className="px-3 py-1.5 text-xs">{r.error ? <span className="text-destructive">{r.error}</span> : r.duplicateOf ? `Matches ${r.duplicateOf}` : "New"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          {preview.errors.length > preview.sample.filter((s) => s.error).length && (
            <details className="text-sm">
              <summary>All rows with errors ({preview.errors.length})</summary>
              <ul className="mt-2 space-y-0.5 text-xs">
                {preview.errors.map((e) => (
                  <li key={e.row}>
                    Row {e.row}: {e.error}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <select aria-label="When a client already exists" className={select} value={onDuplicate} onChange={(e) => setOnDuplicate(e.target.value)}>
              <option value="skip">Skip clients that already exist</option>
              <option value="update">Update existing clients with this file's values</option>
            </select>
            <Button disabled={commit.isPending || preview.totals.ready + (onDuplicate === "update" ? preview.totals.duplicates : 0) === 0} onClick={() => commit.mutate()}>
              Import
            </Button>
          </div>
          {commit.error && <p className="text-sm text-destructive">{errorMessage(commit.error)}</p>}
        </div>
      )}
      {result && (
        <p className="rounded-md bg-muted px-3 py-2 text-sm">
          Imported {result.created} new, updated {result.updated}, skipped {result.skipped}
          {result.errors.length > 0 && `; ${plural(result.errors.length, "row")} contained errors and ${result.errors.length === 1 ? "was" : "were"} not imported`}.
        </p>
      )}
    </div>
  );
}

type DropResult = Array<{ name: string; status: "matched" | "unmatched" | "error"; clientName?: string; reason?: string }>;

export function BulkPdfDrop() {
  const [files, setFiles] = useState<File[]>([]);
  const [folder, setFolder] = useState("Tax Returns");
  // Returns are filed under the year they're filed in: this year's folder by default.
  const latest = latestOpenYear(localToday());
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [share, setShare] = useState(false);
  const [plan, setPlan] = useState<DropResult | null>(null);
  const [done, setDone] = useState<DropResult | null>(null);
  const preview = useMutation({
    meta: { inlineError: true },
    mutationFn: (fs: File[]) => postFile<DropResult>("/api/documents/bulk", fs, { dryRun: "true", folder, year }, "files"),
    onSuccess: setPlan,
  });
  const upload = useMutation({
    meta: { inlineError: true },
    mutationFn: () => postFile<DropResult>("/api/documents/bulk", files, { folder, year, visibility: share ? "shared" : "internal" }, "files"),
    onSuccess: (r) => {
      setDone(r);
      setPlan(null);
    },
  });
  const matched = plan?.filter((p) => p.status === "matched").length ?? 0;
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Upload up to 20 PDFs exported from your tax software. Each file is filed to the client whose <span className="font-medium">client code begins the file name</span>, followed by a space, _ or - (for example <code>1042_2026 1040.pdf</code>). Files without a matching client code are not uploaded. A return goes in the year it is filed: 2026 returns in 2027.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor="drop-folder">Folder</Label>
          <select id="drop-folder" className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={folder} onChange={(e) => setFolder(e.target.value)}>
            {["Tax Returns", "Tax Documents", "Engagement Letters", "Correspondence"].map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="drop-year">Year folder</Label>
          <select id="drop-year" className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={year} onChange={(e) => setYear(e.target.value)}>
            {Array.from({ length: 8 }, (_, i) => latest - i).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} /> Share with clients immediately
        </label>
      </div>
      <input
        type="file"
        multiple
        accept=".pdf"
        aria-label="PDF files"
        onChange={(e) => {
          const fs = [...(e.target.files ?? [])].slice(0, 20);
          setFiles(fs);
          setDone(null);
          if (fs.length) preview.mutate(fs);
        }}
      />
      {preview.error && <p className="text-sm text-destructive">{errorMessage(preview.error)}</p>}
      {(plan ?? done) && (
        <ul className="divide-y divide-border rounded-lg border border-border text-sm">
          {(plan ?? done)!.map((p) => (
            <li key={p.name} className="flex justify-between gap-2 px-3 py-1.5">
              <span className="truncate">{p.name}</span>
              <span className={p.status === "matched" ? "text-emerald-700 dark:text-emerald-300" : "text-muted-foreground"}>
                {p.status === "matched" ? `${done ? "Saved to" : "→"} ${p.clientName}` : p.reason}
              </span>
            </li>
          ))}
        </ul>
      )}
      {plan && (
        <Button disabled={!matched || upload.isPending} onClick={() => upload.mutate()}>
          Upload {matched} matched file{matched === 1 ? "" : "s"}
        </Button>
      )}
      {upload.error && <p className="text-sm text-destructive">{errorMessage(upload.error)}</p>}
    </div>
  );
}

export function FirmSettings({ me }: { me: Me }) {
  const { data } = useQuery<{ timeZone?: string; bankProduct?: { name: string; trackerUrl: string } | null; allow8879WithoutIdentityCheck?: boolean }>({ queryKey: ["/api/firm/settings"] });
  const set8879 = useMutation({ mutationFn: (on: boolean) => api.send("PATCH", "/api/firm/settings", { allow8879WithoutIdentityCheck: on }) });
  const canEdit = hasPermission(me, "firm.manage");
  const [form, setForm] = useState<{ timeZone: string; name: string; url: string } | null>(null);
  const v = form ?? { timeZone: data?.timeZone ?? "America/New_York", name: data?.bankProduct?.name ?? "", url: data?.bankProduct?.trackerUrl ?? "" };
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("PATCH", "/api/firm/settings", { timeZone: v.timeZone, bankProduct: v.name && v.url ? { name: v.name, trackerUrl: v.url } : null }),
    onSuccess: () => setForm(null),
  });
  return (
    <form
      className="max-w-xl space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="tz">Firm time zone (used for due dates)</Label>
        <Input id="tz" disabled={!canEdit} value={v.timeZone} onChange={(e) => setForm({ ...v, timeZone: e.target.value })} />
      </div>
      <p className="pt-2 text-sm font-medium">Refund transfer (bank product) provider</p>
      <p className="text-xs text-muted-foreground">Shown on the refund status of clients whose preparation fees are deducted from their refund.</p>
      <Input aria-label="Partner name" placeholder="Provider name" disabled={!canEdit} value={v.name} onChange={(e) => setForm({ ...v, name: e.target.value })} />
      <Input aria-label="Partner tracker link" placeholder="https://… (the provider's refund status page)" disabled={!canEdit} value={v.url} onChange={(e) => setForm({ ...v, url: e.target.value })} />
      {canEdit && (
        <Button type="submit" disabled={save.isPending || !form}>
          Save
        </Button>
      )}
      {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
      <div className="space-y-2 border-t border-border pt-4">
        <p className="text-sm font-medium">IRS e-file authorization (Form 8879)</p>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            disabled={!canEdit || set8879.isPending}
            checked={!!data?.allow8879WithoutIdentityCheck}
            onChange={async (e) => {
              const on = e.target.checked;
              if (
                !on ||
                (await ask({
                  title: "Allow Form 8879 signing without identity verification?",
                  description:
                    "IRS Publication 1345 requires identity verification (knowledge-based authentication) when a client signs Form 8879 remotely. Enabling this allows clients to sign without it, and the firm accepts that risk. Each signed copy will state that identity was not verified.",
                  confirmLabel: "Allow and accept the risk",
                  destructive: true,
                }))
              )
                set8879.mutate(on);
            }}
          />
          <span>
            Allow clients to sign Forms 8879 and 8878 electronically without identity verification.
            <span className="block text-xs text-muted-foreground">
              When off, clients print, sign and upload the form. IRS Publication 1345 requires identity verification for remote electronic signatures; when on, the firm accepts that risk until an identity verification provider is connected.
            </span>
          </span>
        </label>
      </div>
    </form>
  );
}

export function DataTools({ me }: { me: Me }) {
  return (
    <Tabs defaultValue="import">
      <TabsList className="mb-4 h-auto flex-wrap justify-start">
        {hasPermission(me, "clients.write") && <TabsTrigger value="import">Import clients</TabsTrigger>}
        {hasPermission(me, "documents.write") && <TabsTrigger value="pdfs">Bulk document import</TabsTrigger>}
        {hasPermission(me, "clients.export") && <TabsTrigger value="export">Export</TabsTrigger>}
      </TabsList>
      <TabsContent value="import">
        <Section title="Import clients">
          <ImportClients />
        </Section>
      </TabsContent>
      <TabsContent value="pdfs">
        <Section title="Bulk document import">
          <BulkPdfDrop />
        </Section>
      </TabsContent>
      <TabsContent value="export">
        <Section title="Export clients">
          <p className="mb-3 text-sm text-muted-foreground">A CSV file of all active clients (client code, name, type, email, phone, services) for import into tax software or for backup. Each download is recorded in the audit log.</p>
          <a href="/api/clients/export.csv" className="inline-flex rounded-md border border-border px-4 py-2 text-sm hover:bg-muted">
            Download clients.csv
          </a>
        </Section>
      </TabsContent>
    </Tabs>
  );
}
